import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { FunnelData } from "@/types/invites"

/**
 * OKS-21 — Invite activity chart.
 *
 * Replaces the v1 `<InviteDayTable>` (rejected by CTPO during Phase 3
 * review for being "too long for 7/30d, wasteful when empty"). New
 * shape per UX Spec addendum §2.2 v2: shadcn chart Card surface with
 * a recharts line chart, 4 series (Generated · Clicked · Signup
 * started · Completed), daily counts.
 *
 * **Why line over stacked bar:** stacked bar implies parts-of-a-whole
 * semantics; the 4 stages are independent counts (a creator who
 * `clicked` is also still counted in `generated`). Line chart is
 * truthful — each series stands on its own.
 *
 * **Server-bucketed data.** `funnel.daily` is YYYY-MM-DD strings in
 * brand TZ. FE never does TZ math.
 *
 * **A11y (CRITICAL — UX Spec §2.2 v2):**
 *   - Chart container `role="img"` with summary `aria-label` covering
 *     the range + per-stage totals
 *   - Sibling `<table class="sr-only">` mirroring chart data row-by-
 *     row — screen readers read the table; visual users see the
 *     chart. Playwright also uses the table for stable selectors
 *     (chart canvas testing is fragile).
 *
 * **Empty state.** When `daily.length === 0` OR every series is flat-
 * zero, render a card with the empty message inside the body —
 * **don't collapse the surface** (preserves layout stability).
 *
 * **Loading.** Skeleton variant `<InviteActivityChartSkeleton />`
 * sits at full card height to avoid layout shift on hydrate.
 *
 * **Color tokens.** Series colors come from `--chart-1..4` (shadcn
 * chart system, already in `application.css`). No hardcoded hex.
 * `--primary` (brand orange) is intentionally not used — the chart
 * is data viz, not brand expression.
 */

interface InviteActivityChartProps {
  funnel: FunnelData
  className?: string
}

type SeriesKey = "generated" | "clicked" | "signup_started" | "completed"

interface ChartRow {
  date: string
  generated: number
  clicked: number
  signup_started: number
  completed: number
}

export function InviteActivityChart({
  funnel,
  className,
}: InviteActivityChartProps) {
  const { t } = useTranslation()

  const config: ChartConfig = useMemo(
    () => ({
      generated: {
        label: t("invites.funnel.step.generated"),
        theme: { light: "var(--chart-1)", dark: "var(--chart-1)" },
      },
      clicked: {
        label: t("invites.funnel.step.clicked"),
        theme: { light: "var(--chart-2)", dark: "var(--chart-2)" },
      },
      signup_started: {
        label: t("invites.funnel.step.signup_started"),
        theme: { light: "var(--chart-3)", dark: "var(--chart-3)" },
      },
      completed: {
        label: t("invites.funnel.step.completed"),
        theme: { light: "var(--chart-4)", dark: "var(--chart-4)" },
      },
    }),
    [t],
  )

  const rows: ChartRow[] = funnel.daily
  const isEmpty =
    rows.length === 0 ||
    rows.every(
      (r) =>
        r.generated === 0 &&
        r.clicked === 0 &&
        r.signup_started === 0 &&
        r.completed === 0,
    )

  return (
    <Card
      data-testid="invite-activity-chart"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("invites.activity_chart.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <ChartBody config={config} rows={rows} funnel={funnel} />
        )}
        <SrOnlyTable rows={rows} />
      </CardContent>
    </Card>
  )
}

interface ChartBodyProps {
  config: ChartConfig
  rows: ChartRow[]
  funnel: FunnelData
}

function ChartBody({ config, rows, funnel }: ChartBodyProps) {
  const { t } = useTranslation()
  const ariaSummary = t("invites.activity_chart.aria", {
    range_start: funnel.range_start,
    range_end: funnel.range_end,
    generated: funnel.totals.generated,
    clicked: funnel.totals.clicked,
    signup_started: funnel.totals.signup_started,
    completed: funnel.totals.completed,
  })

  return (
    <div
      data-testid="invite-activity-chart-canvas"
      role="img"
      aria-label={ariaSummary}
    >
      <ChartContainer config={config} className="aspect-auto h-[260px] w-full">
        <LineChart
          data={rows}
          margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={24}
            tickFormatter={(value: string) =>
              new Date(value).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            }
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            allowDecimals={false}
            domain={[0, "auto"]}
            width={32}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {/* `data-testid` may land on a recharts wrapper rather than
              the visible legend element depending on recharts'
              internal SVG node tree (varies between releases). If
              QA's selector is flaky, fall back to selecting via
              the legend items: each rendered series exposes
              `role="listitem"` from recharts' `Legend.Item`. */}
          <ChartLegend
            data-testid="invite-activity-chart-legend"
            content={<ChartLegendContent />}
          />
          {(
            [
              "generated",
              "clicked",
              "signup_started",
              "completed",
            ] as SeriesKey[]
          ).map((key) => (
            <Line
              key={key}
              dataKey={key}
              type="monotone"
              stroke={`var(--color-${key})`}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </div>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="invite-activity-chart-empty"
      role="status"
      className="text-muted-foreground flex h-[260px] flex-col items-center justify-center gap-2 rounded-md border border-dashed text-center text-sm"
    >
      <p className="text-foreground font-medium">
        {t("invites.activity_chart.empty.heading")}
      </p>
      <p>{t("invites.activity_chart.empty.description")}</p>
    </div>
  )
}

interface SrOnlyTableProps {
  rows: ChartRow[]
}

/**
 * Screen-reader-only table mirroring the chart data row-by-row.
 *
 * UX Spec §2.2 v2 a11y requirement: SR users get tabular access to
 * the same data the line chart conveys visually. Playwright also
 * targets this table for stable selectors (chart canvas selectors
 * are fragile — recharts' SVG node tree changes between releases).
 *
 * `class="sr-only"` keeps the table visually hidden but accessible
 * to assistive tech + queryable by tests.
 */
function SrOnlyTable({ rows }: SrOnlyTableProps) {
  const { t } = useTranslation()
  return (
    <table data-testid="invite-activity-chart-data-table" className="sr-only">
      <caption>{t("invites.activity_chart.table_caption")}</caption>
      <thead>
        <tr>
          <th scope="col">{t("invites.activity_chart.col.date")}</th>
          <th scope="col">{t("invites.activity_chart.col.generated")}</th>
          <th scope="col">{t("invites.activity_chart.col.clicked")}</th>
          <th scope="col">{t("invites.activity_chart.col.signup_started")}</th>
          <th scope="col">{t("invites.activity_chart.col.completed")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.date} data-date={row.date}>
            <td>{row.date}</td>
            <td>{row.generated}</td>
            <td>{row.clicked}</td>
            <td>{row.signup_started}</td>
            <td>{row.completed}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Loading skeleton — full card height to avoid layout shift on
 * hydrate. Inertia `<Deferred>` consumes this as `fallback`.
 */
export function InviteActivityChartSkeleton({
  className,
}: {
  className?: string
}) {
  return (
    <Card
      data-testid="invite-activity-chart-loading"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
      aria-busy="true"
      aria-live="polite"
    >
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-[260px] w-full" />
    </Card>
  )
}
